/**
 * Catálogo (F3a) — porte de src/pages/Catalogo do app atual, mesmas regras
 * (packages/core/services/catalogoService): árvore linha → produto → modelos,
 * saldo pelo kardex, specs, imagem da proposta, criar/editar/excluir.
 */
import { createEffect, createResource, createSignal, For, Index, on, Show } from 'solid-js'
import { Button } from '@ui/components/Button'
import { Input } from '@ui/components/Input'
import { Tabs } from '@ui/components/Tabs'
import type { EspecTecnica, ModeloDetalhe } from '@shared/types'
import type { ModeloAvulsoInput, ProdutoCreateInput, ProdutoUpdateInput } from '@shared/validation'
import { api, versaoDados } from '../nucleo/cliente'
import { mensagemErro } from '../lib/erro'
import { AreaTexto, brl, Campo, confirmar, criarRecarga, Erro, Etiqueta, Formulario, lerNumero, ListaDetalhe, Marcar, num, Painel, Secao, Selecao, T, Vazio } from '../ui/kit'

export function Catalogo(props: { podeEditar: boolean }) {
  const [busca, setBusca] = createSignal('')
  const [soSerie, setSoSerie] = createSignal(false)
  const [abaixoMin, setAbaixoMin] = createSignal(false)
  const [inativos, setInativos] = createSignal(false)
  const [linha, setLinha] = createSignal<number | null>(null)
  const [produto, setProduto] = createSignal<number | null>(null)
  const [modeloId, setModeloId] = createSignal<number | null>(null)
  const [cadastrando, setCadastrando] = createSignal(false)
  const [editProduto, setEditProduto] = createSignal<number | null>(null)
  const [erro, setErro] = createSignal<string | null>(null)
  const [recarga, recarregar] = criarRecarga()

  const [arvore] = createResource(() => ({ v: versaoDados(), r: recarga() }), () => api.catalogo.arvore())
  const [modelos] = createResource(
    () => ({ b: busca(), l: linha(), p: produto(), s: soSerie(), a: abaixoMin(), i: inativos(), v: versaoDados(), r: recarga() }),
    (k) =>
      api.catalogo.modelos({
        busca: k.b, linhaId: k.l ?? undefined, produtoId: k.p ?? undefined,
        apenasSerializados: k.s, abaixoMinimo: k.a, incluirInativos: k.i,
      }),
  )

  const excluirLinha = async (id: number, nome: string) => {
    if (!(await confirmar('Excluir linha?', `Excluir a linha "${nome}"? Só é permitido se ela não tiver produtos.`, { rotulo: 'Excluir', perigo: true }))) return
    try {
      await api.catalogo.excluirLinha(id)
      if (linha() === id) setLinha(null)
      recarregar()
    } catch (e) {
      setErro(mensagemErro(e))
    }
  }
  const excluirProduto = async (id: number, nome: string) => {
    if (!(await confirmar('Excluir produto?', `Excluir o produto "${nome}" e todos os seus modelos? Ação permanente.`, { rotulo: 'Excluir', perigo: true }))) return
    try {
      await api.catalogo.excluirProduto(id)
      if (produto() === id) setProduto(null)
      recarregar()
    } catch (e) {
      setErro(mensagemErro(e))
    }
  }

  const item = (ativo: boolean) =>
    `flex-1 min-w-0 rounded px-2 py-1 text-left text-sm truncate ${ativo ? 'bg-accent/15 text-accent' : 'text-ink-muted hover:bg-hover'}`

  const listaEl = (
    <div class="flex flex-col gap-4 p-4 sm:p-6">
      <div class="flex flex-wrap items-center gap-3">
        <div class="mr-auto">
          <h1 class="text-xl font-semibold text-ink">Catálogo de produtos RSBP</h1>
          <p class="text-xs text-ink-subtle">{(modelos.latest ?? []).length} modelo(s) na visão atual</p>
        </div>
        <Show when={props.podeEditar}>
          <Button variant="accent" size="sm" onClick={() => setCadastrando(true)}>Cadastrar produto</Button>
        </Show>
      </div>
      <Erro mensagem={erro()} />
      <div class="grid grid-cols-1 lg:grid-cols-[16rem_1fr] gap-4">
        {/* árvore linha → produto */}
        <aside class="rounded-xl border border-edge bg-panel p-2 self-start">
          <button type="button" class={`w-full mb-1 ${item(!linha() && !produto())}`} onClick={() => (setLinha(null), setProduto(null))}>
            Todos os modelos
          </button>
          <For each={arvore.latest ?? []}>
            {(l) => (
              <div class="mb-1">
                <div class="group flex items-center gap-1">
                  <button
                    type="button"
                    class={`flex-1 rounded px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide ${linha() === l.id ? 'bg-accent/15 text-accent' : 'text-ink-subtle hover:bg-hover'}`}
                    onClick={() => (setProduto(null), setLinha(linha() === l.id ? null : l.id))}
                  >
                    {l.nome}
                  </button>
                  <Show when={props.podeEditar}>
                    <button type="button" title="Excluir linha" class="px-1.5 text-ink-placeholder opacity-0 group-hover:opacity-100 hover:text-failure" onClick={() => void excluirLinha(l.id, l.nome)}>
                      ✕
                    </button>
                  </Show>
                </div>
                <div class="ml-2 mt-0.5 flex flex-col gap-0.5">
                  <For each={l.produtos}>
                    {(p) => (
                      <div class="group flex items-center gap-1">
                        <button type="button" class={`${item(produto() === p.id)} flex items-center justify-between gap-2`} onClick={() => (setLinha(null), setProduto(produto() === p.id ? null : p.id))}>
                          <span class="truncate">{p.nomeComercial}</span>
                          <span class="text-[10px] text-ink-placeholder">{p.qtdModelos}</span>
                        </button>
                        <Show when={props.podeEditar}>
                          <button type="button" title="Editar produto" class="px-1 text-ink-placeholder opacity-0 group-hover:opacity-100 hover:text-accent" onClick={() => setEditProduto(p.id)}>
                            ✎
                          </button>
                          <button type="button" title="Excluir produto" class="px-1 text-ink-placeholder opacity-0 group-hover:opacity-100 hover:text-failure" onClick={() => void excluirProduto(p.id, p.nomeComercial)}>
                            ✕
                          </button>
                        </Show>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            )}
          </For>
        </aside>

        {/* modelos */}
        <section class="flex flex-col gap-3 min-w-0">
          <div class="flex flex-wrap items-center gap-3">
            <Input class="w-64 max-w-full" placeholder="Buscar modelo…" value={busca()} onInput={(e) => setBusca(e.currentTarget.value)} />
            <Marcar checked={soSerie()} onChange={setSoSerie}>Só serializados</Marcar>
            <Marcar checked={abaixoMin()} onChange={setAbaixoMin}>Abaixo do mínimo</Marcar>
            <Marcar checked={inativos()} onChange={setInativos}>Incluir inativos</Marcar>
          </div>
          <Show
            when={(modelos.latest ?? []).length > 0}
            fallback={<Vazio titulo={modelos.loading ? 'Carregando…' : 'Nenhum modelo nesta visão'} texto="Ajuste os filtros ou selecione outra linha/produto." />}
          >
            <div class={T.caixa}>
              <table class={T.tabela}>
                <thead class={T.cab}>
                  <tr>
                    <th class={T.th}>Código</th>
                    <th class={T.th}>Produto</th>
                    <th class={`${T.th} text-right`}>Saldo</th>
                    <th class={`${T.th} text-right`}>Mín.</th>
                    <th class={`${T.th} text-right hidden md:table-cell`}>Custo</th>
                    <th class={`${T.th} text-right`}>Preço</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={modelos.latest}>
                    {(m) => {
                      const baixo = () => m.estoqueMinimo > 0 && m.saldoAtual < m.estoqueMinimo
                      return (
                        <tr class={`${T.tr} cursor-pointer`} classList={{ 'bg-hover': modeloId() === m.id }} onClick={() => setModeloId(m.id)}>
                          <td class={T.td}>
                            <span class="font-medium text-ink">{m.codigoModelo}</span>
                            <Show when={m.serializado === 1}>
                              <span class="ml-2"><Etiqueta tom="aviso">série</Etiqueta></span>
                            </Show>
                            <Show when={m.ativo !== 1}>
                              <span class="ml-2"><Etiqueta tom="perigo">inativo</Etiqueta></span>
                            </Show>
                          </td>
                          <td class={`${T.td} text-ink-muted`}>{m.produtoNome}</td>
                          <td class={`${T.td} text-right tabular-nums font-medium`} classList={{ 'text-warning': baixo(), 'text-ink': !baixo() }}>
                            {num(m.saldoAtual)}
                          </td>
                          <td class={`${T.td} text-right tabular-nums text-ink-subtle`}>{m.estoqueMinimo || '—'}</td>
                          <td class={`${T.td} text-right tabular-nums text-ink-muted hidden md:table-cell`}>{m.custoPadrao != null ? num(m.custoPadrao) : '—'}</td>
                          <td class={`${T.td} text-right tabular-nums text-ink`}>{brl(m.precoVendaPadrao)}</td>
                        </tr>
                      )
                    }}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        </section>
      </div>
    </div>
  )

  return (
    <>
      <ListaDetalhe
        id="catalogo"
        lista={listaEl}
        larguraDetalhe={560}
        detalhe={
          modeloId() !== null && (
            <DetalheModelo
              id={modeloId()!}
              podeEditar={props.podeEditar}
              aoFechar={() => setModeloId(null)}
              aoMudar={recarregar}
              aoExcluido={() => (setModeloId(null), recarregar())}
            />
          )
        }
        aoFecharDetalhe={() => setModeloId(null)}
      />
      <Show when={cadastrando()}>
        <FormProduto aoFechar={() => setCadastrando(false)} aoSalvar={recarregar} />
      </Show>
      <Show when={editProduto() !== null}>
        <EditarProduto id={editProduto()!} aoFechar={() => setEditProduto(null)} aoSalvar={recarregar} />
      </Show>
    </>
  )
}

// ------------------------------------------------------------------ modelo
type EdicaoModelo = {
  codigoModelo: string; descricaoCurta: string; unidadeMedida: string; pesoKg: string; moedaCusto: string
  custoPadrao: string; precoVendaPadrao: string; estoqueMinimo: string; ncm: string; serializado: boolean; ativo: boolean
}
const edicaoDe = (m: ModeloDetalhe): EdicaoModelo => ({
  codigoModelo: m.codigoModelo, descricaoCurta: m.descricaoCurta ?? '', unidadeMedida: m.unidadeMedida,
  pesoKg: m.pesoKg?.toString() ?? '', moedaCusto: m.moedaCusto, custoPadrao: m.custoPadrao?.toString() ?? '',
  precoVendaPadrao: m.precoVendaPadrao?.toString() ?? '', estoqueMinimo: m.estoqueMinimo.toString(), ncm: m.ncm ?? '',
  serializado: m.serializado === 1, ativo: m.ativo === 1,
})

function DetalheModelo(props: { id: number; podeEditar: boolean; aoFechar: () => void; aoMudar: () => void; aoExcluido: () => void }) {
  const [recarga, recarregar] = criarRecarga()
  const [d] = createResource(() => ({ id: props.id, v: versaoDados(), r: recarga() }), ({ id }) => api.catalogo.modelo(id))
  const [imagem, { refetch: recarregarImagem }] = createResource(() => props.id, (id) => api.catalogo.getImagem(id))
  const [ed, setEd] = createSignal<EdicaoModelo | null>(null)
  const [specs, setSpecs] = createSignal<EspecTecnica[]>([])
  const [erro, setErro] = createSignal<string | null>(null)
  const [erroSpecs, setErroSpecs] = createSignal<string | null>(null)
  const [salvando, setSalvando] = createSignal(false)
  const [aba, setAba] = createSignal<'dados' | 'specs'>('dados')
  // recarrega o formulário só quando troca de modelo ou depois de salvar (não a cada sincronização)
  createEffect(on(() => [props.id, recarga(), !!d.latest] as const, () => {
    const m = d.latest
    if (m && m.id === props.id) (setEd(edicaoDe(m)), setSpecs(m.especificacoes.map((e) => ({ ...e }))))
  }))

  const set = <K extends keyof EdicaoModelo>(k: K) => (e: { currentTarget: { value: string } }) => setEd({ ...ed()!, [k]: e.currentTarget.value })

  const salvar = async () => {
    const e = ed()
    if (!e) return
    setErro(null)
    const custo = lerNumero(e.custoPadrao), preco = lerNumero(e.precoVendaPadrao), min = lerNumero(e.estoqueMinimo), peso = lerNumero(e.pesoKg)
    if ([custo, preco, min, peso].some((x) => Number.isNaN(x))) return setErro('Valores numéricos inválidos.')
    if (e.codigoModelo.trim() === '') return setErro('Informe o código do modelo.')
    setSalvando(true)
    try {
      await api.catalogo.atualizarModelo(props.id, {
        codigoModelo: e.codigoModelo.trim(), descricaoCurta: e.descricaoCurta.trim() || null, unidadeMedida: e.unidadeMedida.trim() || 'UN',
        pesoKg: peso, moedaCusto: e.moedaCusto.trim() || 'EUR', custoPadrao: custo, precoVendaPadrao: preco, estoqueMinimo: min ?? 0,
        ncm: e.ncm.trim() || null, serializado: e.serializado, ativo: e.ativo,
      })
      recarregar()
      props.aoMudar()
    } catch (x) {
      setErro(mensagemErro(x))
    } finally {
      setSalvando(false)
    }
  }
  const salvarSpecs = async () => {
    setErroSpecs(null)
    const limpas = specs().map((s) => ({ atributo: s.atributo.trim(), valor: s.valor.trim() })).filter((s) => s.atributo !== '' || s.valor !== '')
    if (limpas.some((s) => s.atributo === '' || s.valor === '')) return setErroSpecs('Preencha atributo e valor de cada especificação (ou remova a linha).')
    try {
      const novas = await api.catalogo.salvarEspecificacoes(props.id, limpas)
      setSpecs(novas.map((e) => ({ ...e })))
      props.aoMudar()
    } catch (x) {
      setErroSpecs(mensagemErro(x))
    }
  }
  const escolherImagem = (e: Event & { currentTarget: HTMLInputElement }) => {
    const arq = e.currentTarget.files?.[0]
    if (!arq) return
    const r = new FileReader()
    r.onload = async () => {
      try {
        await api.catalogo.setImagem(props.id, r.result as string)
        void recarregarImagem()
      } catch (x) {
        setErro(mensagemErro(x))
      }
    }
    r.readAsDataURL(arq)
  }
  const removerImagem = async () => {
    await api.catalogo.removerImagem(props.id)
    void recarregarImagem()
  }
  const excluir = async () => {
    if (!(await confirmar('Excluir modelo?', 'Excluir este modelo do catálogo? Esta ação é permanente.', { rotulo: 'Excluir', perigo: true }))) return
    setErro(null)
    try {
      await api.catalogo.excluirModelo(props.id)
      props.aoExcluido()
    } catch (x) {
      setErro(mensagemErro(x))
    }
  }

  return (
    <Show when={d.latest && ed()} fallback={<p class="p-5 text-sm text-ink-subtle">{d.loading ? 'Carregando…' : 'Modelo não encontrado.'}</p>}>
      <Painel
        titulo={
          <span class="flex flex-wrap items-center gap-2">
            {d.latest!.codigoModelo}
            <Etiqueta tom="destaque">{d.latest!.linhaNome}</Etiqueta>
            <Etiqueta>{d.latest!.produtoNome}</Etiqueta>
            <Show when={d.latest!.serializado === 1}><Etiqueta tom="aviso">Serializado</Etiqueta></Show>
            <Show when={d.latest!.ativo !== 1}><Etiqueta tom="perigo">Inativo</Etiqueta></Show>
          </span>
        }
        sub={
          <>
            Saldo atual: <b class="text-ink">{num(d.latest!.saldoAtual)}</b> {d.latest!.unidadeMedida}
            <Show when={d.latest!.norma}> · Norma: {d.latest!.norma}</Show>
            <Show when={d.latest!.fornecedorNome}> · {d.latest!.fornecedorNome}</Show>
          </>
        }
        aoFechar={props.aoFechar}
        rodape={
          <Show when={props.podeEditar && aba() === 'dados'}>
            <Button variant="danger-ghost" size="sm" onClick={() => void excluir()}>Excluir modelo</Button>
            <Button variant="accent" size="sm" class="ml-auto" disabled={salvando()} onClick={() => void salvar()}>
              {salvando() ? 'Salvando…' : 'Salvar'}
            </Button>
          </Show>
        }
      >
        <Show when={d.latest!.descricaoCurta}>
          <p class="text-sm text-ink-muted -mt-2">{d.latest!.descricaoCurta}</p>
        </Show>
        <Tabs
          aria-label="Seção"
          size="sm"
          value={aba()}
          onChange={setAba}
          options={[
            { value: 'dados', label: 'Identificação e comercial' },
            { value: 'specs', label: 'Especificações e imagem', count: specs().length },
          ]}
        />
        <Erro mensagem={erro()} />
        <Show when={aba() === 'dados'}>
          <fieldset disabled={!props.podeEditar} class="flex flex-col gap-3">
            <Campo rotulo="Código do modelo (SKU)">
              <Input value={ed()!.codigoModelo} onInput={set('codigoModelo')} />
            </Campo>
            <Campo rotulo="Descrição curta">
              <Input value={ed()!.descricaoCurta} onInput={set('descricaoCurta')} />
            </Campo>
            <div class="grid grid-cols-3 gap-3">
              <Campo rotulo="Unidade"><Input value={ed()!.unidadeMedida} onInput={set('unidadeMedida')} /></Campo>
              <Campo rotulo="Peso (kg)"><Input value={ed()!.pesoKg} onInput={set('pesoKg')} placeholder="0,00" /></Campo>
              <Campo rotulo="Moeda"><Input value={ed()!.moedaCusto} onInput={set('moedaCusto')} /></Campo>
            </div>
            <Campo rotulo={`Custo padrão (${d.latest!.moedaCusto}) — FOB`}>
              <Input value={ed()!.custoPadrao} onInput={set('custoPadrao')} placeholder="0,00" />
            </Campo>
            <Campo rotulo="Preço de venda padrão (BRL)" dica={d.latest!.custoLandedBrlPadrao ? `Custo landed conhecido: ${brl(d.latest!.custoLandedBrlPadrao)}` : undefined}>
              <Input value={ed()!.precoVendaPadrao} onInput={set('precoVendaPadrao')} placeholder="0,00" />
            </Campo>
            <div class="grid grid-cols-2 gap-3">
              <Campo rotulo="Estoque mínimo"><Input value={ed()!.estoqueMinimo} onInput={set('estoqueMinimo')} /></Campo>
              <Campo rotulo="NCM"><Input value={ed()!.ncm} onInput={set('ncm')} placeholder="0000.00.00" /></Campo>
            </div>
            <div class="flex gap-5">
              <Marcar checked={ed()!.serializado} onChange={(v) => setEd({ ...ed()!, serializado: v })} disabled={!props.podeEditar}>Serializado</Marcar>
              <Marcar checked={ed()!.ativo} onChange={(v) => setEd({ ...ed()!, ativo: v })} disabled={!props.podeEditar}>Ativo</Marcar>
            </div>
          </fieldset>
        </Show>
        <Show when={aba() === 'specs'}>
          <Secao
            titulo="Especificações técnicas"
            acao={
              <Show when={props.podeEditar}>
                <button type="button" class="text-xs text-accent hover:underline" onClick={() => setSpecs([...specs(), { atributo: '', valor: '' }])}>
                  + adicionar
                </button>
              </Show>
            }
          >
            <Erro mensagem={erroSpecs()} />
            <Show when={specs().length > 0} fallback={<p class="text-sm text-ink-placeholder">Sem especificações cadastradas.</p>}>
              <div class="flex flex-col gap-2">
                <Index each={specs()}>
                  {(s, i) => (
                    <div class="flex gap-2">
                      <Input value={s().atributo} disabled={!props.podeEditar} placeholder="Atributo" onInput={(e) => setSpecs(specs().map((x, n) => (n === i ? { ...x, atributo: e.currentTarget.value } : x)))} />
                      <Input value={s().valor} disabled={!props.podeEditar} placeholder="Valor" onInput={(e) => setSpecs(specs().map((x, n) => (n === i ? { ...x, valor: e.currentTarget.value } : x)))} />
                      <Show when={props.podeEditar}>
                        <button type="button" aria-label="Remover" class="px-1 text-ink-placeholder hover:text-failure" onClick={() => setSpecs(specs().filter((_, n) => n !== i))}>✕</button>
                      </Show>
                    </div>
                  )}
                </Index>
              </div>
            </Show>
            <Show when={props.podeEditar}>
              <div class="flex justify-end">
                <Button variant="secondary" size="sm" onClick={() => void salvarSpecs()}>Salvar especificações</Button>
              </div>
            </Show>
          </Secao>
          <Secao titulo="Imagem (para a proposta)">
            <div class="flex items-center gap-3">
              <Show
                when={imagem()}
                fallback={<div class="h-20 w-24 flex items-center justify-center rounded border border-dashed border-edge text-[10px] text-ink-placeholder">sem imagem</div>}
              >
                <img src={imagem()!} alt="Imagem do modelo" class="h-20 w-24 rounded border border-edge object-contain bg-page" />
              </Show>
              <Show when={props.podeEditar}>
                <div class="flex flex-col gap-1">
                  <label class="cursor-pointer rounded-md border border-edge px-2.5 py-1 text-xs text-ink-muted hover:border-accent">
                    {imagem() ? 'Trocar imagem' : 'Enviar imagem'}
                    <input type="file" accept="image/png,image/jpeg" class="hidden" onChange={escolherImagem} />
                  </label>
                  <Show when={imagem()}>
                    <button type="button" class="text-xs text-failure hover:underline" onClick={() => void removerImagem()}>remover</button>
                  </Show>
                </div>
              </Show>
            </div>
          </Secao>
        </Show>
      </Painel>
    </Show>
  )
}

// ---------------------------------------------------------- produto novo
type RascunhoSpec = { atributo: string; valor: string }
type RascunhoModelo = {
  codigoModelo: string; descricaoCurta: string; unidadeMedida: string; pesoKg: string; ncm: string; moedaCusto: string
  custoPadrao: string; precoVendaPadrao: string; estoqueMinimo: string; serializado: boolean; especificacoes: RascunhoSpec[]
}
const modeloVazio = (): RascunhoModelo => ({
  codigoModelo: '', descricaoCurta: '', unidadeMedida: 'UN', pesoKg: '', ncm: '', moedaCusto: 'EUR',
  custoPadrao: '', precoVendaPadrao: '', estoqueMinimo: '0', serializado: false, especificacoes: [],
})
// '' → null; vírgula decimal aceita. Inválido vira NaN (o Zod do núcleo recusa) — igual ao app atual.
const numOuNull = (s: string) => {
  const t = s.trim().replace(',', '.')
  return t === '' ? null : Number(t)
}
const payloadModelo = (m: RascunhoModelo): Omit<ModeloAvulsoInput, 'produtoId'> => ({
  codigoModelo: m.codigoModelo.trim(), descricaoCurta: m.descricaoCurta.trim() || null, unidadeMedida: m.unidadeMedida.trim() || 'UN',
  pesoKg: numOuNull(m.pesoKg), ncm: m.ncm.trim() || null, moedaCusto: m.moedaCusto.trim() || 'EUR',
  custoPadrao: numOuNull(m.custoPadrao), precoVendaPadrao: numOuNull(m.precoVendaPadrao),
  estoqueMinimo: m.estoqueMinimo.trim() === '' ? 0 : Number(m.estoqueMinimo.trim().replace(',', '.')),
  serializado: m.serializado,
  especificacoes: m.especificacoes.map((e) => ({ atributo: e.atributo.trim(), valor: e.valor.trim() })).filter((e) => e.atributo !== '' && e.valor !== ''),
})

function EscolherLinha(props: { modo: 'existente' | 'nova'; setModo: (m: 'existente' | 'nova') => void; linhaId: string; setLinhaId: (v: string) => void; nova: string; setNova: (v: string) => void }) {
  const [linhas] = createResource(() => api.catalogo.linhas())
  return (
    <div class="grid grid-cols-[1fr_auto] gap-2 items-end">
      <Campo rotulo="Linha / categoria">
        <Show when={props.modo === 'existente'} fallback={<Input value={props.nova} onInput={(e) => props.setNova(e.currentTarget.value)} placeholder="Nome da nova linha" />}>
          <Selecao value={props.linhaId} onChange={(e) => props.setLinhaId(e.currentTarget.value)}>
            <option value="">Selecione…</option>
            <For each={linhas() ?? []}>{(l) => <option value={l.id}>{l.nome}</option>}</For>
          </Selecao>
        </Show>
      </Campo>
      <Button type="button" variant="secondary" size="sm" onClick={() => props.setModo(props.modo === 'existente' ? 'nova' : 'existente')}>
        {props.modo === 'existente' ? '+ Nova linha' : 'Usar existente'}
      </Button>
    </div>
  )
}

function FormProduto(props: { aoFechar: () => void; aoSalvar: () => void }) {
  const [modo, setModo] = createSignal<'produto' | 'modelo'>('produto')
  const [linhaModo, setLinhaModo] = createSignal<'existente' | 'nova'>('existente')
  const [linhaId, setLinhaId] = createSignal('')
  const [novaLinha, setNovaLinha] = createSignal('')
  const [nome, setNome] = createSignal('')
  const [norma, setNorma] = createSignal('')
  const [descricao, setDescricao] = createSignal('')
  const [tag, setTag] = createSignal('')
  const [produtoId, setProdutoId] = createSignal('')
  const [modelos, setModelos] = createSignal<RascunhoModelo[]>([modeloVazio()])
  const [erro, setErro] = createSignal<string | null>(null)
  const [salvando, setSalvando] = createSignal(false)
  const [produtos] = createResource(() => api.catalogo.produtos())

  const mudar = (i: number, p: Partial<RascunhoModelo>) => setModelos(modelos().map((m, n) => (n === i ? { ...m, ...p } : m)))
  const salvar = async () => {
    setErro(null)
    if (modelos().some((m) => m.codigoModelo.trim() === '')) return setErro('Todo modelo precisa de um código.')
    setSalvando(true)
    try {
      if (modo() === 'produto') {
        const payload: ProdutoCreateInput = {
          linhaId: linhaModo() === 'existente' && linhaId() ? Number(linhaId()) : null,
          novaLinha: linhaModo() === 'nova' ? novaLinha().trim() : null,
          nomeComercial: nome().trim(),
          fabricante: 'RSBP spol. s r.o.',
          paisOrigem: 'República Tcheca',
          normaCertificacao: norma().trim() || null,
          descricao: descricao().trim() || null,
          tag: tag().trim() || null,
          modelos: modelos().map(payloadModelo),
        }
        await api.catalogo.criarProduto(payload)
      } else {
        if (!produtoId()) {
          setSalvando(false)
          return setErro('Selecione o produto.')
        }
        for (const m of modelos()) await api.catalogo.criarModelo({ produtoId: Number(produtoId()), ...payloadModelo(m) })
      }
      props.aoSalvar()
      props.aoFechar()
    } catch (x) {
      setErro(mensagemErro(x))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Formulario
      aberto
      titulo="Cadastrar no catálogo"
      largura={820}
      aoFechar={props.aoFechar}
      aoEnviar={() => void salvar()}
      rodape={
        <>
          <Button type="button" variant="ghost" onClick={() => props.aoFechar()}>Cancelar</Button>
          <Button type="submit" variant="accent" disabled={salvando()}>{salvando() ? 'Salvando…' : 'Salvar'}</Button>
        </>
      }
    >
      <Tabs
        aria-label="O que cadastrar"
        size="sm"
        value={modo()}
        onChange={setModo}
        options={[
          { value: 'produto', label: 'Produto novo' },
          { value: 'modelo', label: 'Modelo em produto existente' },
        ]}
      />
      <Erro mensagem={erro()} />
      <Show
        when={modo() === 'produto'}
        fallback={
          <Campo rotulo="Produto existente">
            <Selecao value={produtoId()} onChange={(e) => setProdutoId(e.currentTarget.value)}>
              <option value="">Selecione…</option>
              <For each={produtos() ?? []}>{(p) => <option value={p.id}>{p.nomeComercial}</option>}</For>
            </Selecao>
          </Campo>
        }
      >
        <div class="rounded-lg border border-edge p-4 flex flex-col gap-3">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Campo rotulo="Nome comercial"><Input value={nome()} onInput={(e) => setNome(e.currentTarget.value)} placeholder="Ex.: VENT PRO" /></Campo>
            <Campo rotulo="Norma / certificação"><Input value={norma()} onInput={(e) => setNorma(e.currentTarget.value)} placeholder="Ex.: EN 14797" /></Campo>
          </div>
          <EscolherLinha modo={linhaModo()} setModo={setLinhaModo} linhaId={linhaId()} setLinhaId={setLinhaId} nova={novaLinha()} setNova={setNovaLinha} />
          <Campo rotulo="Etiqueta (tag)" dica="Rótulo livre de classificação — opcional (ex.: Lançamento, Importado).">
            <Input value={tag()} onInput={(e) => setTag(e.currentTarget.value)} />
          </Campo>
          <Campo rotulo="Descrição"><AreaTexto value={descricao()} onInput={(e) => setDescricao(e.currentTarget.value)} /></Campo>
        </div>
      </Show>

      <div class="flex items-center justify-between">
        <span class="text-xs font-medium uppercase tracking-wide text-ink-subtle">Modelos ({modelos().length})</span>
        <Button type="button" variant="secondary" size="sm" onClick={() => setModelos([...modelos(), modeloVazio()])}>+ Adicionar modelo</Button>
      </div>
      <Index each={modelos()}>
        {(m, i) => (
          <div class="rounded-lg border border-edge p-4 flex flex-col gap-3">
            <div class="flex items-center justify-between text-xs text-ink-subtle">
              Modelo {i + 1}
              <Show when={modelos().length > 1}>
                <button type="button" class="text-failure hover:underline" onClick={() => setModelos(modelos().filter((_, n) => n !== i))}>remover</button>
              </Show>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Campo rotulo="Código do modelo (SKU)"><Input value={m().codigoModelo} onInput={(e) => mudar(i, { codigoModelo: e.currentTarget.value })} placeholder="Ex.: VENT-PRO-600" /></Campo>
              <Campo rotulo="Descrição curta"><Input value={m().descricaoCurta} onInput={(e) => mudar(i, { descricaoCurta: e.currentTarget.value })} /></Campo>
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Campo rotulo="Unidade"><Input value={m().unidadeMedida} onInput={(e) => mudar(i, { unidadeMedida: e.currentTarget.value })} /></Campo>
              <Campo rotulo="Peso (kg)"><Input value={m().pesoKg} onInput={(e) => mudar(i, { pesoKg: e.currentTarget.value })} placeholder="0,00" /></Campo>
              <Campo rotulo="NCM"><Input value={m().ncm} onInput={(e) => mudar(i, { ncm: e.currentTarget.value })} placeholder="0000.00.00" /></Campo>
              <Campo rotulo="Estoque mínimo"><Input value={m().estoqueMinimo} onInput={(e) => mudar(i, { estoqueMinimo: e.currentTarget.value })} /></Campo>
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Campo rotulo="Moeda do custo"><Input value={m().moedaCusto} onInput={(e) => mudar(i, { moedaCusto: e.currentTarget.value })} /></Campo>
              <Campo rotulo="Custo padrão (FOB)"><Input value={m().custoPadrao} onInput={(e) => mudar(i, { custoPadrao: e.currentTarget.value })} placeholder="0,00" /></Campo>
              <Campo rotulo="Preço de venda (BRL)"><Input value={m().precoVendaPadrao} onInput={(e) => mudar(i, { precoVendaPadrao: e.currentTarget.value })} placeholder="0,00" /></Campo>
            </div>
            <Marcar checked={m().serializado} onChange={(v) => mudar(i, { serializado: v })}>Serializado (rastreia unidade a unidade)</Marcar>
            <div class="rounded-md border border-edge-muted p-3 flex flex-col gap-2">
              <div class="flex items-center justify-between">
                <span class="text-[11px] font-medium uppercase tracking-wide text-ink-subtle">Especificações técnicas</span>
                <button type="button" class="text-xs text-accent hover:underline" onClick={() => mudar(i, { especificacoes: [...m().especificacoes, { atributo: '', valor: '' }] })}>+ adicionar</button>
              </div>
              <Show when={m().especificacoes.length > 0} fallback={<p class="text-xs text-ink-placeholder">Nenhuma especificação (pode adicionar depois também).</p>}>
                <Index each={m().especificacoes}>
                  {(s, j) => (
                    <div class="flex gap-2">
                      <Input value={s().atributo} placeholder="Atributo" onInput={(e) => mudar(i, { especificacoes: m().especificacoes.map((x, n) => (n === j ? { ...x, atributo: e.currentTarget.value } : x)) })} />
                      <Input value={s().valor} placeholder="Valor" onInput={(e) => mudar(i, { especificacoes: m().especificacoes.map((x, n) => (n === j ? { ...x, valor: e.currentTarget.value } : x)) })} />
                      <button type="button" aria-label="Remover" class="px-1 text-ink-placeholder hover:text-failure" onClick={() => mudar(i, { especificacoes: m().especificacoes.filter((_, n) => n !== j) })}>✕</button>
                    </div>
                  )}
                </Index>
              </Show>
            </div>
          </div>
        )}
      </Index>
    </Formulario>
  )
}

function EditarProduto(props: { id: number; aoFechar: () => void; aoSalvar: () => void }) {
  const [p] = createResource(() => props.id, (id) => api.catalogo.produtoParaEdicao(id))
  const [nome, setNome] = createSignal('')
  const [linhaId, setLinhaId] = createSignal('')
  const [norma, setNorma] = createSignal('')
  const [descricao, setDescricao] = createSignal('')
  const [tag, setTag] = createSignal('')
  const [linhaModo, setLinhaModo] = createSignal<'existente' | 'nova'>('existente')
  const [novaLinha, setNovaLinha] = createSignal('')
  const [erro, setErro] = createSignal<string | null>(null)
  const [salvando, setSalvando] = createSignal(false)
  createEffect(() => {
    const d = p()
    if (d) (setNome(d.nomeComercial), setLinhaId(String(d.linhaId)), setNorma(d.normaCertificacao ?? ''), setDescricao(d.descricao ?? ''), setTag(d.tag ?? ''))
  })
  const salvar = async () => {
    const d = p()
    if (!d) return
    setErro(null)
    setSalvando(true)
    try {
      const payload: ProdutoUpdateInput = {
        nomeComercial: nome().trim(),
        linhaId: linhaModo() === 'existente' && linhaId() ? Number(linhaId()) : null,
        novaLinha: linhaModo() === 'nova' ? novaLinha().trim() : null,
        fabricante: d.fabricante, // preservados (não editados aqui)
        paisOrigem: d.paisOrigem,
        normaCertificacao: norma().trim() || null,
        descricao: descricao().trim() || null,
        tag: tag().trim() || null,
      }
      await api.catalogo.atualizarProduto(props.id, payload)
      props.aoSalvar()
      props.aoFechar()
    } catch (x) {
      setErro(mensagemErro(x))
    } finally {
      setSalvando(false)
    }
  }
  return (
    <Formulario
      aberto
      titulo="Editar produto"
      largura={520}
      aoFechar={props.aoFechar}
      aoEnviar={() => void salvar()}
      rodape={
        <>
          <Button type="button" variant="ghost" onClick={() => props.aoFechar()}>Cancelar</Button>
          <Button type="submit" variant="accent" disabled={salvando() || !p()}>{salvando() ? 'Salvando…' : 'Salvar'}</Button>
        </>
      }
    >
      <Erro mensagem={erro()} />
      <Show when={p()} fallback={<p class="text-sm text-ink-subtle">Carregando…</p>}>
        <Campo rotulo="Nome comercial"><Input value={nome()} onInput={(e) => setNome(e.currentTarget.value)} /></Campo>
        <EscolherLinha modo={linhaModo()} setModo={setLinhaModo} linhaId={linhaId()} setLinhaId={setLinhaId} nova={novaLinha()} setNova={setNovaLinha} />
        <Campo rotulo="Norma / certificação"><Input value={norma()} onInput={(e) => setNorma(e.currentTarget.value)} placeholder="Ex.: EN 14797" /></Campo>
        <Campo rotulo="Etiqueta (tag)"><Input value={tag()} onInput={(e) => setTag(e.currentTarget.value)} /></Campo>
        <Campo rotulo="Descrição"><AreaTexto value={descricao()} onInput={(e) => setDescricao(e.currentTarget.value)} /></Campo>
      </Show>
    </Formulario>
  )
}
